import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Alert, App, Button, ConfigProvider, Input, Skeleton, Tag } from 'antd';
import styled from 'styled-components';
import usePrompt from '../../../hooks/common/usePrompt';
import { asyncSimpleModal } from '../../../lib/common';
import { SEARCH_DICTIONARY_URL_PATH } from '../../../constants/service/common/urlConstants';
import { SynonymDetailResponse, SynonymSaveResponse } from '../../../lib/api/searchDictionary';
import { SettingsWorkspaceStyle } from '../../pgProviderRoute/common/styles';
import { searchDictionaryQuery } from '../queries';
import { useSynonymEditor } from '../hooks/useSynonymEditor';
import { useDeleteSynonymMutation } from '../hooks/useSynonymMutations';
import { readApiError } from '../utils/apiError';
import { ListReturnState, ruleDetailUrl } from '../utils/routes';
import { formatTerms } from '../utils/terms';
import { DictSection } from './DictSection';
import { RuleFeedback } from './RuleFeedback';
import { SaveResultBox } from './SaveResultBox';
import { SEARCH_DICTIONARY_THEME, SearchDictionaryPermissions } from './SearchDictionaryWorkspace';
import { SearchDictionaryStyle } from './styles';
import { SynonymHistoryPanel } from './SynonymHistoryPanel';
import { SynonymPreviewBox } from './SynonymPreviewBox';
import { TermTags } from './TermTags';

/** 카드 아래에 붙는 저장 줄 — 카드 폭 그대로(음수 여백으로 넓히면 사이드바 위로 삐져나온다) */
const StyledSaveBar = styled.footer`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #e7eaec;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.04);
  .spacer {
    flex: 1;
  }
  .unsaved-note {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-right: 8px;
    font-size: 13px;
    color: #8a5a00;
  }
`;

type SynonymRuleWorkspaceProps = SearchDictionaryPermissions & {
  /** 없으면 새 규칙 */
  ruleId?: string;
};

/**
 * 동의어 규칙 상세 · 등록 (S3-D).
 * 모달보다 넓게 본다 — 경고 전문 · 겹치는 줄 바로가기 · 자유 미리보기 · 이 규칙 이력 · 삭제.
 * admin 은 상세 페이지를 두는 게 관례라, 새 규칙도 모달이 아니라 이 화면에서 만든다.
 */
export const SynonymRuleWorkspace = (props: SynonymRuleWorkspaceProps): React.JSX.Element => {
  const { ruleId } = props;
  const query = useQuery({ ...searchDictionaryQuery.synonym(ruleId ?? ''), enabled: Boolean(ruleId) });

  let body: React.ReactNode;
  if (!ruleId) {
    body = <SynonymRuleEditor {...props} rule={null} />;
  } else if (query.isLoading) {
    body = (
      <div className="rule-card-body">
        <Skeleton active />
      </div>
    );
  } else if (query.isError) {
    const notFound = isAxiosError(query.error) && query.error.response?.status === 404;
    body = (
      <div className="rule-card-body">
        <Alert
          type={notFound ? 'warning' : 'error'}
          showIcon
          message={
            notFound ? '이 동의어 규칙을 찾을 수 없습니다. 지워졌을 수 있습니다.' : '규칙을 불러오지 못했습니다.'
          }
          action={!notFound && <Button onClick={() => query.refetch()}>다시 시도</Button>}
        />
      </div>
    );
  } else {
    // key — 다른 줄로 이동하면(겹치는 줄 보기) 편집 상태를 새로 시작한다
    body = <SynonymRuleEditor key={query.data.id} {...props} rule={query.data} />;
  }

  return (
    <ConfigProvider theme={SEARCH_DICTIONARY_THEME}>
      <App>
        <SettingsWorkspaceStyle>
          {/* 탭 화면과 같은 흰 카드 — 회색 바탕 위에 섹션이 떠 있지 않게 */}
          <SearchDictionaryStyle className="rule-card">{body}</SearchDictionaryStyle>
        </SettingsWorkspaceStyle>
      </App>
    </ConfigProvider>
  );
};

type SynonymRuleEditorProps = SearchDictionaryPermissions & { rule: SynonymDetailResponse | null };

const SynonymRuleEditor = (props: SynonymRuleEditorProps): React.JSX.Element => {
  const { rule, canCreate, canUpdate, canDelete } = props;
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationState = location.state as (ListReturnState & { savedResult?: SynonymSaveResponse }) | null;
  const listUrl = navigationState?.listUrl;
  // 등록 화면에서 저장하고 넘어왔다면 그 결과(건수 전/후 · 되돌리기)를 이어서 보여 준다 — 한 번만 쓴다
  const [initialLastSave] = useState(() => navigationState?.savedResult ?? null);
  useEffect(() => {
    if (navigationState?.savedResult) navigate('.', { replace: true, state: { listUrl } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isNew = rule === null;
  const canEdit = isNew ? canCreate : canUpdate;

  const onSaved = (response: SynonymSaveResponse): void => {
    // 새 규칙은 저장되면 그 규칙의 상세로 — 뒤로가기에 빈 등록 화면이 남지 않게 바꿔치기한다
    if (isNew && response.rule) {
      void message.success('등록했습니다. 바로 반영됐습니다.');
      allowNextNavigation();
      navigate(ruleDetailUrl(response.rule.id), { replace: true, state: { listUrl, savedResult: response } });
      return;
    }
    // 등록을 되돌려 줄이 지워졌다 — 남을 상세가 없으니 목록으로
    if (!isNew && response.rule === null) {
      void message.success('등록을 되돌렸습니다.');
      allowNextNavigation();
      navigate(listUrl ?? SEARCH_DICTIONARY_URL_PATH);
    }
  };
  const editor = useSynonymEditor({ rule, onSaved, initialLastSave });
  const deleteMutation = useDeleteSynonymMutation();
  // 요청 중에도 확인한다(실패하면 입력을 잃지 않게). 성공한 뒤의 이동은 allowNextNavigation 으로 보낸다
  const { allowNextNavigation } = usePrompt(
    editor.isDirty,
    '저장하지 않은 동의어 변경이 있습니다. 이 페이지를 나가시겠습니까?',
  );

  const goToList = (): void => navigate(listUrl ?? SEARCH_DICTIONARY_URL_PATH);
  const openRule = (ruleId: string): void => navigate(ruleDetailUrl(ruleId), { state: location.state });

  const handleDelete = async (): Promise<void> => {
    if (!rule) return;
    const isConfirmed = await asyncSimpleModal({
      type: 'confirm',
      title: '이 줄을 지울까요?',
      message: `「${formatTerms(rule.terms)}」\n동의어라 바로 검색에 반영됩니다. 변경 이력에서 되돌릴 수 있습니다.`,
    });
    if (!isConfirmed) return;
    try {
      await deleteMutation.mutateAsync({ ruleId: rule.id, previousTerms: editor.baseTerms });
      void message.success('지웠습니다. 바로 반영됐습니다.');
      // 지운 줄이라 남을 게 없다 — 확인 없이 목록으로
      allowNextNavigation();
      goToList();
    } catch (error) {
      void message.error(readApiError(error, '지우지 못했습니다.').message);
    }
  };

  // 머리의 경고는 저장된 상태 — 입력 중인 초안의 경고는 토큰 칸 아래에 따로 보인다
  const savedWarningCount = rule?.warnings.length ?? 0;

  return (
    <>
      <div className="rule-card-body">
        <div className="rule-head">
          <Button type="link" onClick={goToList} className="rule-head-back">
            ‹ 목록
          </Button>
          <span className="rule-head-divider" aria-hidden="true" />
          <h2>{isNew ? '새 동의어 규칙' : `${rule.no}번 줄`}</h2>
          {savedWarningCount > 0 && <Tag color="orange">경고 {savedWarningCount}건</Tag>}
          {rule?.isMapping && <Tag color="blue">매핑</Tag>}
          {!isNew && <span className="rule-id rule-id-chip">{rule.id}</span>}
        </div>

        {!canEdit && (
          <Alert
            type="info"
            showIcon
            message={`조회 권한으로 보고 있습니다. ${isNew ? '등록' : '수정'} 권한이 필요합니다.`}
          />
        )}
        {!editor.isEditable && (
          <Alert
            type="info"
            showIcon
            message="명시적 매핑(=>) 규칙은 화면에서 고칠 수 없습니다. 지운 뒤 새로 등록해 주세요."
          />
        )}

        {/* 넓은 화면은 2열(편집 | 미리보기) — 고치면서 바로 옆에서 결과를 본다 */}
        <div className="settings-two-columns">
          <div className="rule-column">
            <DictSection
              title="토큰"
              description="같은 뜻의 말을 쉼표로 구분해 적습니다. 저장하면 바로 검색에 반영됩니다."
            >
              <div className="field-stack">
                <Input.TextArea
                  aria-label="토큰"
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  value={editor.text}
                  // 저장·삭제 중엔 잠근다 — 보낸 뒤에 고친 내용은 성공 응답(또는 이동)에 덮여 사라진다
                  disabled={!canEdit || !editor.isEditable || editor.isSaving || deleteMutation.isLoading}
                  placeholder="예: 팬던트, 펜던트, pendant"
                  onChange={(event) => editor.setText(event.target.value)}
                />
                {editor.terms.length > 0 && (
                  <div className="term-tags-row">
                    <span className="settings-muted">토큰 {editor.terms.length}개</span>
                    <TermTags terms={editor.terms} warnings={editor.warnings} />
                  </div>
                )}
                <RuleFeedback
                  errors={editor.errors}
                  warnings={editor.warnings}
                  conflict={editor.conflict}
                  message={editor.message}
                  onReload={editor.reload}
                  onClickRule={openRule}
                />
              </div>
            </DictSection>

            {editor.lastSave && !isNew && (
              <SaveResultBox result={editor.lastSave} onUndo={() => void editor.undo()} isUndoing={editor.isSaving} />
            )}

            {rule && rule.overlaps.length > 0 && (
              <DictSection
                title="토큰이 겹치는 줄"
                description="두 줄의 말이 서로 엮여 검색이 넓어집니다. 합치거나 한쪽에서 빼는 걸 검토하세요."
              >
                <ul className="overlap-list">
                  {rule.overlaps.map((overlap) => (
                    <li key={overlap.id}>
                      <Button type="link" size="small" onClick={() => openRule(overlap.id)}>
                        {overlap.no}번 줄
                      </Button>
                      <TermTags terms={overlap.terms} warnings={overlap.warnings} />
                    </li>
                  ))}
                </ul>
              </DictSection>
            )}
          </div>

          <div className="rule-column">
            <DictSection
              title="미리보기"
              description="고객이 이렇게 검색하면 어떤 말로 나뉘고 무엇과 함께 찾아지는지 저장 전에 봅니다."
            >
              <SynonymPreviewBox
                initialText={editor.terms[0] ?? rule?.terms[0] ?? ''}
                draft={editor.isDirty ? { terms: editor.terms, ruleId: rule?.id } : undefined}
              />
            </DictSection>
          </div>
        </div>

        {rule && (
          <DictSection title="이 규칙 이력">
            <SynonymHistoryPanel ruleId={rule.id} canRollback={canCreate && canUpdate && canDelete} />
          </DictSection>
        )}
      </div>

      <StyledSaveBar>
        {!isNew && canDelete && (
          <Button danger loading={deleteMutation.isLoading} onClick={() => void handleDelete()}>
            삭제
          </Button>
        )}
        <span className="spacer" />
        {editor.isDirty && (
          <span className="unsaved-note">
            <span className="settings-unsaved" aria-hidden="true" />
            저장하지 않은 변경
          </span>
        )}
        <Button onClick={goToList}>목록</Button>
        {canEdit && editor.isEditable && (
          <Button
            type="primary"
            disabled={!editor.canSave}
            loading={editor.isSaving}
            onClick={() => void editor.save()}
          >
            {editor.saveLabel}
          </Button>
        )}
      </StyledSaveBar>
    </>
  );
};
